export const getCompanyPath = () => {
  return window.location.pathname.split("/")[1];
}

export const base64ToBlob = (base64Data: string): Blob => {
  const byteString = atob(base64Data.split(',')[1]);
  const mimeType = base64Data.split(',')[0].split(':')[1].split(';')[0];
  const byteNumbers = new Array(byteString.length).fill(0).map((_, i) => byteString.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};


export const getFileSize = (size: number) => {
  if(size < 1024 * 1024) {
    return Math.round(size / 1024) + 'KB';
  }
  
  return Math.round(size / 1024 / 1024) + 'MB';
}

/**
 * 파일이 실제 PDF인지 매직바이트(파일 시그니처)로 검사한다.
 *
 * file.type(MIME)은 파일 내용이 아니라 업로드하는 클라이언트 OS/레지스트리의
 * 확장자-MIME 매핑에서 채워지는 값이라, PC 환경에 따라 빈 문자열이나 비표준 값
 * (application/x-pdf, application/octet-stream 등)이 될 수 있어 단독 판별 기준으로
 * 신뢰할 수 없다. 따라서 파일 앞부분의 "%PDF-" 시그니처를 직접 확인한다.
 *
 * @param file 검사할 파일
 * @returns 실제 PDF이면 true
 */
export const isPdfFile = async (file: File): Promise<boolean> => {
  try {
    const header = new Uint8Array(await file.slice(0, 5).arrayBuffer());
    return String.fromCharCode(...header) === '%PDF-';
  } catch {
    // 매직바이트를 읽을 수 없는 환경에서는 확장자/MIME로 폴백한다.
    return file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
  }
}

/**
 * 파일이 실제 이미지인지 매직바이트(파일 시그니처)로 검사한다.
 *
 * isPdfFile과 동일한 이유로 file.type(MIME)은 클라이언트 환경에 따라 신뢰할 수 없어,
 * PNG/JPEG/GIF/WEBP/BMP 시그니처를 파일 앞부분에서 직접 확인한다.
 *
 * @param file 검사할 파일
 * @returns 실제 이미지이면 true
 */
export const isImageFile = async (file: File): Promise<boolean> => {
  try {
    const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    // PNG (89 50 4E 47)
    if (hex.startsWith('89504e47')) return true;
    // JPEG (FF D8 FF)
    if (hex.startsWith('ffd8ff')) return true;
    // GIF (47 49 46 38 = "GIF8")
    if (hex.startsWith('47494638')) return true;
    // BMP (42 4D = "BM")
    if (hex.startsWith('424d')) return true;
    // WEBP ("RIFF" .... "WEBP")
    if (hex.startsWith('52494646') && hex.slice(16, 24) === '57454250') return true;
    return false;
  } catch {
    // 매직바이트를 읽을 수 없는 환경에서는 확장자/MIME로 폴백한다.
    return /\.(png|jpe?g|gif|webp|bmp)$/.test(file.name.toLowerCase()) || file.type.startsWith('image/');
  }
}

export const popupPdfViewer = (pdfId: number) => {
  window.open(`/${getCompanyPath()}/pdf-viewer?pdf_id=${pdfId}`, '_blank', 'fullscreen=yes,width='+screen.availWidth+',height='+screen.availHeight);
}

export const removeSpaces = (str: string) => {
  return str.replace(/\s/g, '');
}


/**
 * 히스토리가 있으면 뒤로 가고, 없으면 창을 닫는 함수
 * @param navigate React Router의 navigate 함수
 * @param onClose 창 닫기 콜백 함수 (선택적)
 */
export const navigateBackOrClose = (navigate: any, onClose?: () => void) => {
  // React Router의 navigation state(idx)는 SPA 내부 이동에서만 증가하고,
  // 새로고침이나 하드 내비게이션(전체 문서 로드)으로 진입하면 0으로 리셋된다.
  // 따라서 idx만으로 판단하면 "히스토리가 있는데도" 뒤로 못 간다고 오판한다.
  const historyIdx: number = (window.history.state as { idx?: number } | null)?.idx ?? -1;

  // 같은 출처(same-origin)에서 넘어온 경우인지 확인 (외부 이메일 링크 등 직접 진입과 구분)
  let sameOriginReferrer = false;
  try {
    sameOriginReferrer =
      !!document.referrer && new URL(document.referrer).origin === window.location.origin;
  } catch {
    sameOriginReferrer = false;
  }

  // SPA 내부 이동(idx > 0)이거나,
  // 브라우저 히스토리가 있고 같은 출처에서 진입한 경우(새로고침/하드 내비게이션 포함) 뒤로 이동 가능으로 판단
  const canGoBack = historyIdx > 0 || (window.history.length > 1 && sameOriginReferrer);

  console.log('canGoBack', canGoBack);
  console.log('historyIdx', historyIdx);
  console.log('window.history.state', window.history.state);

  if (!canGoBack) {
    if (onClose) {
      onClose();
    } else {
      window.close();
      try {
        if (!window.closed) {
          alert('브라우저 정책으로 인해 창을 자동으로 닫을 수 없습니다.\n창을 직접 닫아주세요.');
        }
      } catch (e) {
        // ignore
      }
    }
  } else {
    navigate(-1);
  }
};


/**
 * 기업 이름에서 법인 표시 및 특수문자를 제거하는 함수
 * @param companyName 기업 이름
 * @returns 법인 표시 및 특수문자가 제거된 기업 이름
 */
export const cleanCompanyName = (companyName: string): string => {
  if (!companyName) return '';

  // (주), (재), (주식회사), (유), (유한회사), (사), (사단법인), (재단법인), 주식회사, 유한회사, 재단법인, 사단법인, ㈜, ㈈, 재, (주), (재), (유), (사), (재단법인) 등 패턴 제거
  let cleanedName = companyName
    .replace(
      /(\(주\)|\(재\)|\(주식회사\)|\(유\)|\(유한회사\)|\(사\)|\(사단법인\)|\(재단법인\))/g,
      ''
    ) // 괄호 포함 형태
    .replace(
      /(^주식회사|^유한회사|^재단법인|^사단법인|^㈜|^㈈|^재|^사|^(주)|^(재)|^(유)|^(사)|^(\(재단법인\)))/g,
      ''
    ) // 문자열 시작(앞)에 등장하는 패턴
    .replace(
      /(\s+주식회사|\s+유한회사|\s+재단법인|\s+사단법인|\s+㈜|\s+㈈|\s+재|\s+사|\s+(주)|\s+(재)|\s+(유)|\s+(사)|\s+\(재단법인\))$/g,
      ''
    ); // 문자열 끝(뒤)에 등장하는 패턴

  cleanedName = cleanedName.trim();

  return cleanedName;
};


/**
 * 생년월일과 성별을 받아서 생년월일-성별 형식으로 반환하는 함수
 * @param birth 생년월일
 * @param gender 성별
 * @returns 생년월일-성별 형식의 문자열
 */
export const getBirthGender = (birth: string, gender: string) => {
  if(!birth || !gender) return '';

  const year = birth.slice(0, 4);
  const shortBirth = birth.slice(2); // YYMMDD
  let genderCode = '';
  if (parseInt(year, 10) >= 2000) {
    genderCode = gender === 'M' ? '3' : '4';
  } else {
    genderCode = gender === 'M' ? '1' : '2';
  }
  return `${shortBirth}-${genderCode}`;
}


// 아이디 유효성 검사
export const validateLoginId = (loginId: string): string | null => {
  if (!loginId.trim()) {
    return '아이디를 입력해주세요.';
  }
  if (loginId.length < 4) {
    return '아이디는 4자 이상이어야 합니다.';
  }
  if (loginId.length > 20) {
    return '아이디는 20자 이하여야 합니다.';
  }
  
  const hasLetter = /[a-zA-Z]/.test(loginId);
  const isValidFormat = /^[a-zA-Z0-9]+$/.test(loginId);
  
  if (!isValidFormat) {
    return '아이디는 영문과 숫자만 사용할 수 있습니다.';
  }
  if (!hasLetter) {
    return '아이디는 영문을 반드시 포함해야 합니다.';
  }
  
  return null;
}

// 비밀번호 유효성 검사
export const validatePassword = (password: string): string | null => {
  const length = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    const validTypes = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
    const isValid = length && validTypes >= 2;
    if(isValid) {
      return null;
    }
    
    return '비밀번호는 8자 이상이어야 하며, 영문, 숫자, 특수문자 중 2가지 이상 조합이어야 합니다.';
};


/**
 * 직원 목록을 우선순위에 따라 정렬합니다.
 * 우선순위: 대표이사 > 핵심인력 > 일반 직원
 * 각 그룹 내에서는 입사일 오름차순 정렬 (먼저 입사한 사람이 위로)
 * 
 * @param employees 정렬할 직원 목록
 * @returns 정렬된 직원 목록 (원본 배열을 변경하지 않음)
 */
export const sortEmployeesByPriority = <T extends { 
  isRepresentative?: boolean; 
  isCoreOperator?: boolean; 
  joinedAt?: string;
}>(employees: T[]): T[] => {
  return [...employees].sort((a, b) => {
    // 1. 대표이사 우선
    if (a.isRepresentative && !b.isRepresentative) return -1;
    if (!a.isRepresentative && b.isRepresentative) return 1;
    
    // 2. 핵심인력 우선
    if (a.isCoreOperator && !b.isCoreOperator) return -1;
    if (!a.isCoreOperator && b.isCoreOperator) return 1;
    
    // 3. 입사일 오름차순 (먼저 입사한 사람이 위로)
    const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : Infinity;
    const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : Infinity;
    return dateA - dateB;
  });
};